/**
 * Team Inbox — compact clickable chat header + Actions surface (the header-menu
 * redesign, AVBP_TEAMINBOX_HEADER_MENU_MOCKUP_2026-06-22).
 *
 * Replaces the stacked action-bar + triage-strip + always-on Context panel with:
 *   • a COMPACT header (avatar · Name · number/email · project chip · ⚠ Unsorted
 *     chip when unmatched · ⋯) — the whole header is ONE tap target;
 *   • an ACTIONS surface opened on tap — DESKTOP a right slide-over (~328px) that
 *     MERGES the Context rows with the action groups, MOBILE a bottom sheet
 *     (grabber · slide-up · backdrop-dismiss · safe-area padding · 44px targets).
 *
 * Used by BOTH the WhatsApp Thread and the Email read pane so the two channels
 * share one action vocabulary. Presentation-only: the host screen supplies the
 * conversation, the resolved received-on/contact, the action handlers, and the
 * LIVE-vs-LATER capability of each action.
 *
 * Channel/desktop/mobile are CSS-driven (one DOM, two layouts) — the slide-over
 * and the sheet are the same `.ti-actions` element positioned by media query, so
 * there is no JS viewport branch to drift. Capacitor/touch-safe: no hover-only
 * reveals; open/close is an explicit tap.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { InboxConversation, Teammate } from '../lib/types';
import type { LiveClient } from '../../../lib/projectStore';
import type { LinkedLeadChip } from '../../../lib/leadThreadJoin';
import { contextRows, headerIdentity, type ReceivedOn } from '../lib/headerMenu';
import type { TriageAction } from '../screens/EmailThread';

interface MenuItem {
  key: string;
  cls?: string;
  icon: string;
  label: string;
  sub?: string;
  chev?: boolean;
  /** LIVE → wired onClick. LATER → disabled + "ships soon" tooltip (no dead click). */
  live: boolean;
  onClick?: () => void;
}

export interface ThreadHeaderActions {
  onCreateLead?: () => void;
  onCreateContact?: () => void;
  onAddToExisting?: () => void;
  onArchive?: () => void;
  onHold?: () => void;
  onUnhold?: () => void;
  onAssign?: (toStaffId: string, name: string) => void;
  /** Enter multi-select forward mode (pick several received files to forward). */
  onSelectForward?: () => void;
}

export default function ThreadHeader({
  conversation,
  received,
  resolved,
  lead = null,
  myId,
  isAdmin,
  canAssign,
  teammates,
  linkProject = true,
  open,
  onOpen,
  onClose,
  onBack,
  busy = false,
  isHeld = false,
  actions,
}: {
  conversation: InboxConversation;
  received: ReceivedOn | null;
  resolved: LiveClient | null;
  /** AD-039 identity join — the lead this conversation EXACT-matches (phone/
   *  email). Renders the lead chip + Context row; null = no change at all. */
  lead?: LinkedLeadChip | null;
  myId: string | null;
  isAdmin: boolean;
  canAssign: boolean;
  teammates: Teammate[];
  linkProject?: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onBack: () => void;
  busy?: boolean;
  isHeld?: boolean;
  actions: ThreadHeaderActions;
}) {
  const id = headerIdentity(conversation, received, resolved);
  const ctx = contextRows(conversation, received, resolved, myId, lead);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const sortItems: MenuItem[] = id.unsorted
    ? [
        {
          key: 'lead',
          cls: 'lead',
          icon: '＋',
          label: 'Create Lead',
          sub: 'New sales lead from this sender',
          live: !!actions.onCreateLead,
          onClick: actions.onCreateLead,
        },
        {
          key: 'contact',
          cls: 'contact',
          icon: '＋',
          label: 'Create Contact',
          sub: 'Add to the people directory',
          live: !!actions.onCreateContact,
          onClick: actions.onCreateContact,
        },
        {
          key: 'existing',
          icon: '🔎',
          label: 'Add to existing',
          sub: 'Link to a contact or lead already in AVBP',
          chev: true,
          live: !!actions.onAddToExisting,
          onClick: actions.onAddToExisting,
        },
        {
          key: 'archive',
          icon: '🗄',
          label: 'Archive',
          sub: 'Not relevant — file it away',
          live: !!actions.onArchive,
          onClick: actions.onArchive,
        },
        isHeld
          ? {
              key: 'unhold',
              cls: 'warn',
              icon: '↩',
              label: 'Un-hold',
              sub: 'Return to the active inbox',
              live: !!actions.onUnhold,
              onClick: actions.onUnhold,
            }
          : {
              key: 'hold',
              cls: 'warn',
              icon: '⏸',
              label: 'Hold',
              sub: 'Decide later',
              live: !!actions.onHold,
              onClick: actions.onHold,
            },
      ]
    : [];

  const manageItems: MenuItem[] = [
    // Multi-select forward — INCLUDED only when the host can actually run the
    // loop (main inbox). Absent entirely on the lens surfaces (LensThread mounts
    // Thread without onForwardMany) so no dead "LATER" roadmap row ever renders.
    ...(actions.onSelectForward
      ? [
          {
            key: 'forward-select',
            icon: '↪',
            label: 'Select messages to forward',
            sub: 'Pick several received messages and send them to another chat',
            live: true,
            onClick: actions.onSelectForward,
          } as MenuItem,
        ]
      : []),
    { key: 'attach', icon: '🔗', label: 'Attach to project', sub: conversation.projectRef ? `Currently ${conversation.projectRef}` : 'Link to a project', chev: true, live: false },
    { key: 'task', icon: '✓', label: 'Add task', sub: 'Create a follow-up task', live: false },
    { key: 'note', icon: '🗒', label: 'Internal note', sub: 'Team-only, never sent', live: false },
    { key: 'ai', icon: '✨', label: 'AI summarise', sub: 'Recap this conversation', live: false },
    { key: 'snooze', icon: '⏰', label: 'Snooze', sub: 'Mute until later', live: false },
    { key: 'spam', cls: 'danger', icon: '🚫', label: 'Spam / block', sub: 'Block this sender', live: false },
  ];

  const assignList = isAdmin ? teammates : teammates.filter((t) => t.id === myId);

  return (
    <>
      {/* compact, clickable header (one tap target) */}
      <div
        className="ti-chead"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
      >
        <button
          type="button"
          className="ti-chead-back"
          aria-label="Back to list"
          onClick={(e) => {
            e.stopPropagation();
            onBack();
          }}
        >
          ‹
        </button>
        <span className={`ti-chead-av ${id.avatarClass}`} aria-hidden>
          {id.avatar}
        </span>
        <span className="ti-chead-who">
          <b>
            {id.name}
            {id.ref && <span className="ti-chead-ref"> · {id.ref}</span>}
          </b>
          <span className="ti-chead-sub">
            {id.identity && <span className="ti-chead-ident">{id.identity}</span>}
            {id.chips.map((ch, i) => (
              <span key={i} className={`ti-hchip ${ch.cls}`}>
                {ch.text}
              </span>
            ))}
            {/* AD-039 — joined-lead chip (exact identity match only). One
                click opens the lead record; unlinked threads render nothing. */}
            {lead &&
              (linkProject ? (
                <Link
                  to={`/leads/${lead.id}`}
                  className="ti-hchip lead"
                  title={`Open lead record — ${lead.name} · ${lead.stageLabel}`}
                  aria-label={`Open lead record for ${lead.name}, stage ${lead.stageLabel}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  Lead · {lead.name} · {lead.stageLabel} ↗
                </Link>
              ) : (
                <span className="ti-hchip lead">
                  Lead · {lead.name} · {lead.stageLabel}
                </span>
              ))}
          </span>
        </span>
        <span className="ti-chead-hint" aria-hidden>
          tap for actions ›
        </span>
        <button
          type="button"
          className="ti-chead-more"
          aria-label="Actions"
          title="Actions"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          ⋯
        </button>
      </div>

      {/* actions surface — desktop slide-over / mobile bottom sheet (one DOM) */}
      <div className={`ti-actions-host${open ? ' open' : ''}`}>
        <div className="ti-actions-backdrop" onClick={onClose} aria-hidden />
        <aside className="ti-actions" role="dialog" aria-label="Conversation actions">
          <div className="ti-actions-grabber" aria-hidden />
          <div className="ti-actions-head">
            <span className={`ti-chead-av ${id.avatarClass}`} aria-hidden>
              {id.avatar}
            </span>
            <span className="ti-actions-who">
              <b>{id.name}</b>
              <span>
                {id.unsorted
                  ? conversation.channel === 'email'
                    ? 'Unsorted sender'
                    : 'Unsorted number'
                  : id.ref || ''}
              </span>
            </span>
            <button
              type="button"
              className="ti-actions-x"
              onClick={onClose}
              aria-label="Close actions"
            >
              ✕
            </button>
          </div>

          <div className="ti-actions-body">
            {sortItems.length > 0 && (
              <div className="ti-mgroup sort">
                <p className="ti-ghd">
                  Sort this {conversation.channel === 'email' ? 'sender' : 'number'}
                  <span className="ti-gline" />
                </p>
                {sortItems.map((it) => (
                  <MenuButton key={it.key} item={it} busy={busy} onClose={onClose} />
                ))}
              </div>
            )}

            <div className="ti-mgroup">
              <p className="ti-ghd">
                Manage<span className="ti-gline" />
              </p>
              {canAssign && (
                <div className="ti-mgroup-assign">
                  <p className="ti-assign-sub">👤 Assign — hand this chat to a teammate</p>
                  {assignList.length === 0 && (
                    <div className="ti-assign-menu-empty">No teammates available.</div>
                  )}
                  {assignList.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="ti-assign-menu-item"
                      disabled={
                        busy ||
                        (conversation.owner != null && conversation.owner?.uid === t.id)
                      }
                      onClick={() => {
                        actions.onAssign?.(t.id, t.name);
                        onClose();
                      }}
                    >
                      {myId && t.id === myId ? `${t.name} (you)` : t.name}
                      {conversation.owner?.uid === t.id && (
                        <span className="ti-assign-away">current</span>
                      )}
                      {!t.available && <span className="ti-assign-away">away</span>}
                    </button>
                  ))}
                </div>
              )}
              {manageItems.map((it) => (
                <MenuButton key={it.key} item={it} busy={busy} onClose={onClose} />
              ))}
            </div>

            <div className="ti-actions-ctx">
              <p className="ti-ghd ctx">Context<span className="ti-gline" /></p>
              {ctx.map((r) => (
                <div key={r.k} className="ti-ctx-crow">
                  <span className="ti-ctx-ck">{r.k}</span>
                  <span className="ti-ctx-cv">
                    {r.href && linkProject ? (
                      <Link to={r.href} onClick={onClose}>
                        {r.v} · Open ↗
                      </Link>
                    ) : (
                      r.v
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

function MenuButton({
  item,
  busy,
  onClose,
}: {
  item: MenuItem;
  busy: boolean;
  onClose: () => void;
}) {
  if (!item.live) {
    return (
      <button
        type="button"
        className={`ti-mitem dim${item.cls ? ` ${item.cls}` : ''}`}
        disabled
        title={`${item.label} — ships in Phase 4`}
      >
        <span className="ti-mic">{item.icon}</span>
        <span className="ti-mlab">
          {item.label}
          {item.sub && <span className="ti-msub">{item.sub}</span>}
        </span>
        <span className="ti-b-later">LATER</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className={`ti-mitem${item.cls ? ` ${item.cls}` : ''}`}
      disabled={busy}
      onClick={() => {
        item.onClick?.();
        onClose();
      }}
    >
      <span className="ti-mic">{item.icon}</span>
      <span className="ti-mlab">
        {item.label}
        {item.sub && <span className="ti-msub">{item.sub}</span>}
      </span>
      {item.chev && <span className="ti-chev">›</span>}
    </button>
  );
}

export type { TriageAction };
